import BaseTooltip from 'components/BaseTooltip'
import { chain } from 'lodash'
import React from 'react'
import { InvestorLead } from 'scenes/Dashboard/data/fundingEvents/types'
import { FundingRound, Investment } from 'scenes/Dashboard/data/fundingRounds/types'
import { Organization } from 'scenes/Dashboard/data/organizations/types'
import { formatArray, pluralize } from 'utils'
import { format, formatDistance } from 'utils-next/dates'
import { groupCurrency } from 'utils/currency'

import {
  createStyles,
  Hidden,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'

const FundingRoundsTable: React.SFC<FundingRoundsTableProps & WithStyles<typeof styles>> = ({
  classes,
  organization,
  fundingRounds,
  prepend,
}) => {
  const mergedFundingRounds = fundingRounds ? [...fundingRounds] : []

  if (!fundingRounds && !organization?.fundingEvent) {
    return null
  }

  return (
    <>
      {prepend}
      <Table className={classes.fundingTable}>
        <TableHead>
          <TableRow>
            <TableCell colSpan={4} size="small">
              Announced Date
            </TableCell>
            <Hidden smDown>
              <TableCell colSpan={4} size="small">
                Time since
                <br />
                previous round
              </TableCell>
            </Hidden>
            <TableCell colSpan={3} size="small">
              Round Type
            </TableCell>
            <TableCell colSpan={3} size="small">
              Money
              <br />
              Raised
            </TableCell>
            <TableCell colSpan={5} size="small">
              Investors
            </TableCell>
            <TableCell colSpan={5} size="small">
              Committed Investors
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {chain(mergedFundingRounds)
            .filter((fundingRound) => !!fundingRound.announcedOn)
            .sortBy((fundingRound) => fundingRound.announcedOn)
            .reverse()
            .value()
            .map((fundingRound, index, array) => {
              const previousRound = array[index + 1]
              return (
                <TableRow key={index}>
                  <TableCell colSpan={4} size="small">
                    {fundingRound.announcedOn ? format(new Date(fundingRound.announcedOn), 'MMM d, yyyy') : '-'}
                  </TableCell>
                  <Hidden smDown>
                    <TableCell colSpan={4} size="small">
                      {previousRound && fundingRound.announcedOn && previousRound.announcedOn
                        ? formatDistance(new Date(fundingRound.announcedOn), new Date(previousRound.announcedOn))
                        : 'N/A'}
                    </TableCell>
                  </Hidden>
                  <TableCell colSpan={3} size="small">
                    {fundingRound.roundType || '–'}
                  </TableCell>
                  <TableCell colSpan={3} size="small">
                    {groupCurrency(fundingRound.roundSize, fundingRound.roundSizeCurrency)}
                  </TableCell>
                  <TableCell colSpan={5} size="small">
                    {displayInvestors(classes, fundingRound.investments)}
                  </TableCell>
                  <TableCell colSpan={5} size="small">
                    {fundingRound.committedInvestors || '-'}
                  </TableCell>
                </TableRow>
              )
            })}
        </TableBody>
      </Table>
    </>
  )
}

function getInvestorName(investment: Investment | InvestorLead): string {
  if ('id' in investment) {
    if (investment.organization) {
      return investment.organization.name
    }
    if (investment.person) {
      return `${investment.person.firstName} ${investment.person.lastName}`
    }
    return ''
  }
  return investment.investorName
}

function displayInvestors(classes: WithStyles<typeof styles>['classes'], investments?: (Investment | InvestorLead)[]) {
  if (!investments || investments.length === 0) {
    return <Typography variant="body2">–</Typography>
  } else if (investments.length === 1) {
    const onlyInvestment = investments[0]
    return <Typography variant="body2">{getInvestorName(onlyInvestment)}</Typography>
  }

  const leadInvestorsNames = investments
    .filter((investment: Investment | InvestorLead) => investment.isLead)
    .map(getInvestorName)
  const hasLeadInvestors = leadInvestorsNames.length > 0
  const nonLeadInvestorsNames = investments
    .filter((investment: Investment | InvestorLead) => !investment.isLead)
    .map(getInvestorName)
  const hasNonLeadInvestors = nonLeadInvestorsNames.length > 0

  if (hasLeadInvestors && hasNonLeadInvestors) {
    return (
      <>
        <Typography variant="body2">{formatArray(leadInvestorsNames)}</Typography>{' '}
        <Typography>as {pluralize(leadInvestorsNames.length, 'lead', 'leads')} and </Typography>
        <BaseTooltip title={formatArray(nonLeadInvestorsNames)}>
          <span className={classes.tooltippedText}>
            {nonLeadInvestorsNames.length} {pluralize(nonLeadInvestorsNames.length, 'other', 'others')}
          </span>
        </BaseTooltip>
      </>
    )
  } else {
    const firstInvestorName = getInvestorName(investments[0])
    const restInvestorNames = investments.slice(1).map(getInvestorName)
    return (
      <>
        <Typography variant="body2">{firstInvestorName}</Typography> and{' '}
        <BaseTooltip title={formatArray(restInvestorNames)}>
          <span className={classes.tooltippedText}>
            {restInvestorNames.length} {pluralize(restInvestorNames.length, 'other', 'others')}
          </span>
        </BaseTooltip>
      </>
    )
  }
}

export type FundingRoundsTableProps = {
  children?: React.ReactNode
  organization?: Organization
  fundingRounds?: FundingRound[]
  prepend?: React.ReactNode
}

const styles = ({ spacing, breakpoints }: Theme) =>
  createStyles({
    fundingTable: {
      marginBottom: spacing(2),
      [breakpoints.up('xs')]: {
        tableLayout: 'fixed',
      },
    },
    tooltippedText: {
      textDecoration: 'underline',
      textDecorationStyle: 'dotted',
    },
  })

export default withStyles(styles)(FundingRoundsTable)
